import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HeaderService } from 'core';
import { CustomerDetailService, DeliveryDestinations } from 'customers';
import { LoadingSpinner } from 'design-system';
import { finalize } from 'rxjs';
import { isManualCustomer } from '../../interfaces/company-search-result.model';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatInputModule, MatRadioModule, MatSlideToggleModule, RouterLink, LoadingSpinner, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer.html',
  styleUrl: './configure-customer.scss',
})
export class ConfigureCustomer implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  // TODO: Collegare la ricerca dei destini alla futura API. Per ora il campo è solo dimostrativo.
  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  readonly creatingDestination = signal(false);
  readonly noteMaxLength = 65;
  readonly existingDestinationForm = inject(FormBuilder).nonNullable.group({
    riferimento: [''],
    giorni: [''],
    orari: [''],
    contatto: [''],
    notaLibera: ['', Validators.maxLength(this.noteMaxLength)],
    inBolla: [false],
    sponda: [false],
  });
  readonly newDestinationForm = inject(FormBuilder).nonNullable.group({
    riferimento: ['', [Validators.required, Validators.pattern(/\S/)]],
    citta: ['', [Validators.required, Validators.pattern(/\S/)]],
    provincia: ['', [Validators.required, Validators.pattern(/^[a-zA-Z]{2}$/)]],
    cap: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
    indirizzo: ['', [Validators.required, Validators.pattern(/\S/)]],
    giorni: [''],
    orari: [''],
    contatto: [''],
    notaLibera: [''],
    inBolla: [false],
    sponda: [false],
  });
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));

    // Lo state mantiene il cliente selezionato anche ricaricando questa pagina.
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);

    if (this.customer() && !this.configuration.destinationsLoaded()) {
      this.loadDestinations();
    }
    this.restoreDestinationDetails();
    const newDestination = this.configuration.newDestination();
    if (newDestination) {
      this.newDestinationForm.setValue({
        riferimento: newDestination.riferimento ?? '',
        citta: newDestination.citta,
        provincia: newDestination.provincia,
        cap: newDestination.cap,
        indirizzo: newDestination.indirizzo,
        giorni: newDestination.nota.giorni ?? '',
        orari: newDestination.nota.orari ?? '',
        contatto: newDestination.nota.contatto ?? '',
        notaLibera: newDestination.nota.notaLibera ?? '',
        inBolla: newDestination.nota.inBolla,
        sponda: newDestination.nota.sponda ?? false,
      });
      this.creatingDestination.set(true);
    } else {
      const customer = this.customer();
      if (customer && isManualCustomer(customer)) {
        this.creatingDestination.set(true);
        if (this.configuration.useManualAddressAsDestination()) {
          this.newDestinationForm.patchValue({
            citta: customer.localita,
            provincia: customer.provincia,
            cap: customer.cap,
            indirizzo: customer.indirizzo,
          });
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  loadDestinations(): void {
    const customer = this.customer();
    if (!customer || this.loading()) {
      return;
    }
    if (isManualCustomer(customer)) {
      this.configuration.setDestinations([]);
      this.loadFailed.set(false);
      return;
    }
    this.loading.set(true);
    this.loadFailed.set(false);
    this.customerDetailService.loadDeliveryDestinations(customer.idBusinessPartner).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: destinations => {
        this.configuration.setDestinations(destinations ?? []);
        this.restoreDestinationDetails();
      },
      error: () => this.loadFailed.set(true),
    });
  }

  selectDestination(id: string): void {
    if (this.configuration.selectedDestinationId() === id || !this.configuration.destinations().some(destination => destination.id === id)) {
      return;
    }
    this.saveDestinationDetails();
    this.configuration.newDestination.set(null);
    this.configuration.selectedDestinationId.set(id);
    this.restoreDestinationDetails();
  }

  openNewDestination(): void {
    this.saveDestinationDetails();
    this.creatingDestination.set(true);
  }

  backToDestinations(): void {
    this.creatingDestination.set(false);
  }

  mapUrl(destination: DeliveryDestinations): string {
    const address = [destination.indirizzo, destination.cap, destination.citta, destination.provincia].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  continue(): void {
    if (!this.customer()) {
      return;
    }
    if (this.creatingDestination()) {
      this.newDestinationForm.markAllAsTouched();
      if (this.newDestinationForm.invalid) {
        return;
      }
      const value = this.newDestinationForm.getRawValue();
      // Nessuna API di creazione disponibile: il nuovo destino è salvato solo nella bozza del flusso.
      this.configuration.setNewDestination({
        riferimento: value.riferimento.trim(),
        citta: value.citta.trim(),
        provincia: value.provincia.toUpperCase(),
        cap: value.cap,
        indirizzo: value.indirizzo.trim(),
        nota: {
          giorni: value.giorni.trim() || null,
          orari: value.orari.trim() || null,
          contatto: value.contatto.trim() || null,
          notaLibera: value.notaLibera.trim() || null,
          inBolla: value.inBolla,
          sponda: value.sponda,
        },
      });
    } else {
      if (!this.configuration.selectedDestination() || this.loading() || this.loadFailed()) {
        return;
      }
      this.existingDestinationForm.markAllAsTouched();
      if (this.existingDestinationForm.invalid) {
        return;
      }
      this.saveDestinationDetails();
    }
    this.advanceConfiguration();
  }

  private restoreDestinationDetails(): void {
    const destination = this.configuration.selectedDestination();
    if (!destination) {
      return;
    }
    this.existingDestinationForm.reset({
      riferimento: destination.riferimento ?? '',
      giorni: destination.nota?.giorni ?? '',
      orari: destination.nota?.orari ?? '',
      contatto: destination.nota?.contatto ?? '',
      notaLibera: destination.nota?.notaLibera ?? '',
      inBolla: destination.nota?.inBolla ?? false,
      sponda: destination.nota?.sponda ?? false,
    });
  }

  private saveDestinationDetails(): void {
    const destination = this.configuration.selectedDestination();
    if (!destination) {
      return;
    }
    const value = this.existingDestinationForm.getRawValue();
    // Le modifiche appartengono solo alla bozza di configurazione, senza aggiornare l'anagrafica.
    this.configuration.setDestinationDetails(destination.id, {
      riferimento: value.riferimento.trim() || null,
      nota: {
        giorni: value.giorni.trim() || null,
        orari: value.orari.trim() || null,
        contatto: value.contatto.trim() || null,
        notaLibera: value.notaLibera.trim() || null,
        inBolla: value.inBolla,
        sponda: value.sponda,
      },
    });
  }

  private advanceConfiguration(): void {
    // Sia la selezione di un destino esistente sia il nuovo destino proseguono da questo punto.
    void this.router.navigate(['/new-order-proposal/configure-customer/zones'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }
}
